// @ts-check
// ── Settings → Integrations → Voice: business name on caller ID (CNAM) ──
//
// ⚠️ ONLY TWILIO'S ANSWER MOVES THE BADGE. Register sends the name for review
// (twilio-register-cnam.js); cnam-check-status.js writes Twilio's answer onto
// the coach's sms channel row, and this paints it. There is no way here to
// mark a name approved. The window this replaces had one, sent coaches to a
// Twilio Console they cannot open, and nothing was ever registered.
//
// A name that is already approved can be changed (Clayton, 2026-10-01). The
// new name goes for review on its own; calls keep the approved name until
// the carriers approve the new one.
//
// The words a coach reads say "carriers", never "Twilio".

const MAX_NAME_LENGTH = 15;
// Twilio: up to 72 hours for an approved name to reach every US carrier.
const REACH_DAYS = 3;

/**
 * @typedef {{ ok: true, name: string }} NameAccepted
 * @typedef {{ ok: false, problem: 'empty'|'too_long'|'starts_with_non_letter'|'bad_characters', message: string }} NameRefused
 * @typedef {{ badge: { text: string, tone: ''|'pending'|'verified'|'error' }, line: string|null, button: string|null }} RowView
 */

/**
 * The carriers' rules, with the same words as the server's copy
 * (netlify/functions/_shared/caller-id-name.js); a test holds the two equal.
 * @param {unknown} raw
 * @returns {NameAccepted|NameRefused}
 */
export function checkDisplayName(raw) {
  const name = typeof raw === 'string' ? raw.trim().replace(/\s+/g, ' ') : '';
  if (!name) return { ok: false, problem: 'empty', message: 'Type the name you want clients to see.' };
  if (name.length > MAX_NAME_LENGTH) {
    return { ok: false, problem: 'too_long', message: `Caller ID shows ${MAX_NAME_LENGTH} characters at most. This is ${name.length}.` };
  }
  if (!/^[A-Za-z]/.test(name)) return { ok: false, problem: 'starts_with_non_letter', message: 'Start with a letter.' };
  const bad = name.match(/[^A-Za-z0-9 .,]/);
  if (bad) {
    return { ok: false, problem: 'bad_characters', message: `Use only letters, numbers, spaces, periods and commas. Remove the ${bad[0]}.` };
  }
  return { ok: true, name };
}

/** @param {unknown} number */
function isTollFree(number) {
  let digits = String(number ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return digits.length === 10 && /^8(00|33|44|55|66|77|88)/.test(digits);
}

/**
 * The approved name calls show today, or null. A name the OLD card marked
 * "approved" with nothing registered has no Twilio registration, so it is not
 * a live name.
 * @param {Record<string, any>} c
 */
function liveName(c) {
  return c.cnam_trust_product_sid && c.cnam_business_name ? String(c.cnam_business_name) : null;
}

/**
 * What the row says for the coach's sms channel row.
 * @param {Record<string, any>|null|undefined} channel
 * @returns {RowView}
 */
export function callerIdNameView(channel) {
  const c = channel || {};
  if (!c.twilio_phone_number) {
    return { badge: { text: 'Not registered', tone: '' }, line: 'Get a business number first. It is the number your calls come from.', button: null };
  }
  if (isTollFree(c.twilio_phone_number)) {
    return { badge: { text: 'Not available', tone: '' }, line: "Caller ID names aren't available for toll-free numbers.", button: null };
  }
  if (!c.a2p_customer_profile_sid) {
    return { badge: { text: 'Not registered', tone: '' }, line: 'Finish your texting registration first. Your caller ID name uses the same business details.', button: null };
  }
  const live = liveName(c);
  const pending = c.cnam_pending_business_name || '';
  const why = c.cnam_failure_reason ? ` The reason given: ${c.cnam_failure_reason}` : ' No reason was given.';
  /** @type {RowView['badge']} */
  const approved = { text: 'Approved', tone: 'verified' };

  if (c.cnam_status === 'pending') {
    return live
      ? { badge: approved, line: `Calls show "${live}". Your new name, "${pending}", is in review.`, button: null }
      : { badge: { text: 'In review', tone: 'pending' }, line: `"${pending}" is with the carriers for review. It usually takes a few business days.`, button: null };
  }
  if (c.cnam_status === 'rejected') {
    return live
      ? { badge: approved, line: `Calls show "${live}". Your new name, "${pending}", wasn't approved.${why}`, button: 'Try another name' }
      : { badge: { text: 'Not approved', tone: 'error' }, line: `The carriers turned down "${pending}".${why}`, button: 'Try again' };
  }
  if (live) {
    const since = Date.parse(c.cnam_registered_at || '');
    const recent = Number.isFinite(since) && Date.now() - since < REACH_DAYS * 24 * 60 * 60 * 1000;
    return { badge: approved, line: `Calls show "${live}".${recent ? ' Some carriers take up to 3 days to show it.' : ''}`, button: 'Change name' };
  }
  return { badge: { text: 'Not registered', tone: '' }, line: null, button: 'Register' };
}

const TONE_CLASS = { '': '', pending: ' email-domain-status--pending', verified: ' email-domain-status--verified', error: ' email-domain-status--error' };

/**
 * Paint the caller ID name row.
 * @param {Record<string, any>|null|undefined} channel the coach's sms channel row
 */
export function renderCallerIdName(channel) {
  const view = callerIdNameView(channel);
  const badge = document.getElementById('cnam-status-badge');
  if (badge) {
    badge.textContent = view.badge.text;
    badge.className = 'email-domain-status' + TONE_CLASS[view.badge.tone];
  }
  const line = document.getElementById('cnam-status-line');
  if (line) {
    line.textContent = view.line || '';
    line.classList.toggle('hidden', !view.line);
  }
  const btn = document.getElementById('btn-cnam-manage');
  if (btn) {
    btn.textContent = view.button || '';
    btn.classList.toggle('hidden', !view.button);
  }
}

/**
 * Wire the caller ID name window.
 * @param {{
 *   callFunction: (name: string, body: Record<string, any>) => Promise<any>,
 *   showToast: (message: string, kind?: string) => void,
 *   reload: () => void,
 *   businessName: () => string,
 * }} deps businessName is read when the window opens (settings load after boot)
 */
export function initCallerIdName({ callFunction, showToast, reload, businessName }) {
  /** @param {string} id */
  const $ = (id) => document.getElementById(id);
  const input = () => /** @type {HTMLInputElement|null} */ ($('cnam-business-name-input'));
  const submit = () => /** @type {HTMLButtonElement|null} */ ($('btn-cnam-submit'));

  function showError(/** @type {string} */ text) {
    const box = $('cnam-submit-error');
    if (!box) return;
    box.textContent = text;
    box.classList.toggle('hidden', !text);
  }

  // The counter, the problem and Send follow what is typed. The count is of
  // the name as it will be sent (ends trimmed, runs of spaces as one).
  function refresh() {
    const typed = input()?.value ?? '';
    const checked = checkDisplayName(typed);
    const count = $('cnam-name-count');
    if (count) count.textContent = `${typed.trim().replace(/\s+/g, ' ').length} of ${MAX_NAME_LENGTH}`;
    const problem = $('cnam-name-problem');
    if (problem) problem.textContent = checked.ok || checked.problem === 'empty' ? '' : checked.message;
    const btn = submit();
    if (btn) btn.disabled = !checked.ok;
    showError('');
  }

  function open() {
    const c = /** @type {any} */ (window)._voiceComplianceChannel || {};
    const live = liveName(c);
    const start = c.cnam_status === 'rejected' && c.cnam_pending_business_name
      ? c.cnam_pending_business_name
      : (live || businessName() || '');
    const el = input();
    if (el) el.value = start;
    const title = $('cnam-modal-title');
    if (title) title.textContent = live ? 'Change your caller ID name' : 'Business name on caller ID';
    const note = $('cnam-modal-note');
    if (note) {
      note.textContent = live ? `Calls keep showing "${live}" until the carriers approve the new name.` : '';
      note.classList.toggle('hidden', !live);
    }
    refresh();
    $('cnam-modal-overlay')?.classList.remove('hidden');
    el?.focus();
  }

  function close() {
    $('cnam-modal-overlay')?.classList.add('hidden');
  }

  async function send() {
    const checked = checkDisplayName(input()?.value);
    if (!checked.ok) return;
    const btn = submit();
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    try {
      await callFunction('twilio-register-cnam', { name: checked.name });
      showToast("Sent for review. You'll get a notification when the carriers answer.", 'success');
      close();
      reload();
    } catch (err) {
      const e = /** @type {any} */ (err);
      showError(e?.message || 'Something went wrong. Try again.');
      // Turned down on the spot: the row now carries the reason.
      if (e?.status === 422) reload();
    } finally {
      if (btn) { btn.textContent = 'Send for review'; btn.disabled = !checkDisplayName(input()?.value).ok; }
    }
  }

  $('btn-cnam-manage')?.addEventListener('click', open);
  $('cnam-modal-close')?.addEventListener('click', close);
  $('btn-cnam-cancel')?.addEventListener('click', close);
  $('btn-cnam-submit')?.addEventListener('click', () => { send(); });
  input()?.addEventListener('input', refresh);
  input()?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && submit()?.disabled === false) { e.preventDefault(); send(); }
  });
  $('cnam-modal-overlay')?.addEventListener('click', (e) => {
    if (/** @type {HTMLElement|null} */ (e.target)?.id === 'cnam-modal-overlay') close();
  });
}
